import React, { useState } from 'react';
import { Sparkles, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Button } from './ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

/**
 * <EventTitleField /> — Iter-182.
 *
 * Drop-in form field for the public-facing "Event Title" surfaced across
 * Discover, push notifications, email subjects, and event channel names.
 *
 * Props:
 *   value: string
 *   onChange: (next: string) => void
 *   role_name?: string         (used for AI generation context)
 *   event_type?: string
 *   venue_name?: string
 *   city?: string
 *   state?: string
 *   notes?: string
 *   required?: boolean         (default false; backend validates 5..120 when set)
 *   testIdPrefix?: string      (defaults to 'event-title')
 */
const EventTitleField = ({
  value,
  onChange,
  role_name,
  event_type,
  venue_name,
  city,
  state,
  notes,
  required = false,
  testIdPrefix = 'event-title',
}) => {
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const trimmed = (value || '').trim();
  const tooShort = trimmed.length > 0 && trimmed.length < 5;
  const tooLong = trimmed.length > 120;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/event-title/generate', {
        role_name: role_name || null,
        event_type: event_type || null,
        venue_name: venue_name || null,
        city: city || null,
        state: state || null,
        notes: notes || null,
      });
      const list = Array.isArray(data?.suggestions) ? data.suggestions : [];
      setSuggestions(list);
      if (list.length === 0) toast.info('No suggestions returned');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not generate titles');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-field`}>
      <div className="flex items-center justify-between">
        <Label htmlFor="event_title" className="text-gray-700">
          Event Title{required ? ' *' : <span className="text-xs text-gray-400 ml-1">(optional)</span>}
        </Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleGenerate}
          disabled={generating}
          className="text-xs"
          data-testid={`${testIdPrefix}-ai-btn`}
        >
          {generating ? (
            <Loader2 className="w-3 h-3 animate-spin mr-1" />
          ) : (
            <Sparkles className="w-3 h-3 mr-1" />
          )}
          Generate with AI
        </Button>
      </div>
      <Input
        id="event_title"
        type="text"
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="e.g. VIP Mansion Cocktail Party"
        maxLength={120}
        required={required}
        className={`bg-white border-gray-300 text-gray-900 ${
          tooShort || tooLong ? 'border-rose-400' : ''
        }`}
        data-testid={`${testIdPrefix}-input`}
      />
      <div className="flex items-center justify-between text-xs">
        <p className="text-gray-500">
          Talent see this everywhere — discover cards, emails, push, channels. Falls back to "{role_name || 'Role'} Gig" if blank.
        </p>
        <p className={tooLong ? 'text-rose-500' : 'text-gray-400'}>
          {trimmed.length}/120
        </p>
      </div>
      {tooShort && (
        <p className="text-xs text-rose-500" data-testid={`${testIdPrefix}-min-error`}>
          Minimum 5 characters
        </p>
      )}
      {suggestions.length > 0 && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-md p-2 space-y-1" data-testid={`${testIdPrefix}-suggestions`}>
          <p className="text-xs font-medium text-cyan-700">Tap to use one:</p>
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s, i) => (
              <button
                key={i}
                type="button"
                onClick={() => { onChange(s); setSuggestions([]); }}
                className="text-xs bg-white border border-cyan-200 rounded-full px-3 py-1 hover:bg-cyan-100 transition"
                data-testid={`${testIdPrefix}-suggestion-${i}`}
              >
                {s}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTitleField;
